import React, { useState, useEffect } from 'react';
import { 
  Target, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Eye, 
  Edit,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Calendar,
  MapPin,
  DollarSign,
  Users,
  X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../../config/api';

export default function NGOCampaignManagement() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFeaturedModal, setShowFeaturedModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [processingId, setProcessingId] = useState(null);
  const [newStatus, setNewStatus] = useState('');

  // Fetch campaigns from backend
  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching campaigns from admin API');
      
      // The token is already set in the api configuration
      const response = await api.get('/admin/projects');
      
      console.log('Campaigns fetched successfully:', response.data);
      setCampaigns(response.data);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError('Failed to load campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Handle campaign status update
  const handleStatusUpdate = async (campaignId, newStatus) => {
    setProcessingId(campaignId);
    try {
      console.log(`Updating campaign ${campaignId} status to ${newStatus}`);
      
      // Use the API configuration which already has the token set
      const response = await api.patch(`/admin/projects/${campaignId}/status`, { status: newStatus });
      
      console.log('Campaign status updated:', response.data);
      
      // Update the campaign in the local state
      setCampaigns(prevCampaigns => 
        prevCampaigns.map(campaign => 
          campaign._id === campaignId ? { ...campaign, status: newStatus } : campaign
        )
      );
      
      // Close the modal and show success message
      setShowStatusModal(false);
      toast.success('Campaign status updated successfully');
    } catch (err) {
      console.error('Error updating campaign status:', err);
      toast.error(err.response?.data?.msg || 'Failed to update campaign status');
    } finally {
      setProcessingId(null);
    }
  };

  // Handle toggling featured status
  const handleToggleFeatured = async (campaignId, featured) => {
    setProcessingId(campaignId);
    try {
      console.log(`Setting campaign ${campaignId} featured status to ${featured}`);
      
      // Use the API configuration which already has the token set
      const response = await api.patch(`/admin/projects/${campaignId}/featured`, { featured });
      
      console.log('Campaign featured status updated:', response.data);
      
      // Update the campaign in the local state
      setCampaigns(prevCampaigns => 
        prevCampaigns.map(campaign => 
          campaign._id === campaignId ? { ...campaign, featured } : campaign
        )
      );
      
      // Close the modal and show success message
      setShowFeaturedModal(false);
      toast.success(`Campaign ${featured ? 'added to' : 'removed from'} homepage slider`);
    } catch (err) {
      console.error('Error updating campaign featured status:', err);
      toast.error(err.response?.data?.msg || 'Failed to update featured status');
    } finally {
      setProcessingId(null);
    }
  };

  // Filter campaigns based on search term and status filter
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (campaign.ngo && campaign.ngo.name && campaign.ngo.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCampaigns = filteredCampaigns.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);

  // Status badge color mapping
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <Target className="mr-2" size={24} />
          NGO Campaign Management
        </h1>
        <button
          onClick={fetchCampaigns}
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <RefreshCw size={16} className="mr-1" />
          Refresh
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center">
          <span className="mr-2 text-gray-700">
            <Filter size={18} />
          </span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2"
          >
            <option value="all">All Campaigns</option>
            <option value="pending">Pending Approval</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="relative w-full md:w-64">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search size={18} className="text-gray-500" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search campaigns or NGOs..."
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
          />
        </div>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}

      {/* Campaigns Table */}
      {!loading && !error && (
        <>
          {filteredCampaigns.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <div className="flex justify-center mb-4">
                <AlertCircle size={48} className="text-gray-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-700 mb-2">No campaigns found</h3>
              <p className="text-gray-500 mb-4">
                {statusFilter !== 'all' 
                  ? `There are no campaigns with status "${statusFilter}".` 
                  : searchTerm 
                    ? `No campaigns match your search "${searchTerm}".` 
                    : "No campaigns have been created yet."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NGO</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Featured</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentCampaigns.map((campaign) => (
                    <tr key={campaign._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 mr-3">
                            <img 
                              className="h-10 w-10 rounded-full object-cover" 
                              src={campaign.image || 'https://via.placeholder.com/40'} 
                              alt={campaign.title} 
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{campaign.title}</div>
                            <div className="text-sm text-gray-500">{campaign.category}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{campaign.ngo?.name || 'Unknown NGO'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(campaign.status)}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${campaign.featured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {campaign.featured ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(campaign.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setShowDetailsModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setNewStatus(campaign.status);
                              setShowStatusModal(true);
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Update Status"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setShowFeaturedModal(true);
                            }}
                            className="text-yellow-600 hover:text-yellow-900"
                            title={campaign.featured ? "Remove from Homepage" : "Add to Homepage"}
                          >
                            <Target size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {filteredCampaigns.length > itemsPerPage && (
            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                <span className="font-medium">
                  {indexOfLastItem > filteredCampaigns.length ? filteredCampaigns.length : indexOfLastItem}
                </span>{' '}
                of <span className="font-medium">{filteredCampaigns.length}</span> results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Campaign Details Modal */}
      {showDetailsModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h2 className="text-2xl font-bold text-gray-800">{selectedCampaign.title}</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle size={24} />
                </button>
              </div>
              
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <img 
                    src={selectedCampaign.image || 'https://via.placeholder.com/400x200'} 
                    alt={selectedCampaign.title}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-gray-800">Campaign Details</h3>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center text-gray-700">
                        <MapPin size={16} className="mr-2" />
                        <span>{selectedCampaign.location || 'No location specified'}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Calendar size={16} className="mr-2" />
                        <span>
                          {formatDate(selectedCampaign.startDate)} - {formatDate(selectedCampaign.endDate)}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <DollarSign size={16} className="mr-2" />
                        <span>
                          Goal: ₹{selectedCampaign.goalAmount?.toLocaleString() || 0} | 
                          Raised: ₹{selectedCampaign.raisedAmount?.toLocaleString() || 0}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Users size={16} className="mr-2" />
                        <span>Beneficiaries: {selectedCampaign.beneficiaries || 'Not specified'}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Description</h3>
                    <p className="mt-2 text-gray-600">{selectedCampaign.description}</p>
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">NGO Information</h3>
                    <div className="mt-2">
                      <p className="text-gray-700">
                        <strong>Name:</strong> {selectedCampaign.ngo?.name || 'Unknown'}
                      </p>
                      {selectedCampaign.ngo?.email && (
                        <p className="text-gray-700">
                          <strong>Email:</strong> {selectedCampaign.ngo.email}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Status Information</h3>
                    <div className="mt-2">
                      <p className="text-gray-700">
                        <strong>Current Status:</strong>{' '}
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(selectedCampaign.status)}`}>
                          {selectedCampaign.status}
                        </span>
                      </p>
                      <p className="text-gray-700 mt-1">
                        <strong>Created:</strong> {formatDate(selectedCampaign.createdAt)}
                      </p>
                      <p className="text-gray-700 mt-1">
                        <strong>Last Updated:</strong> {formatDate(selectedCampaign.updatedAt)}
                      </p>
                    </div>
                    
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          setShowDetailsModal(false);
                          setShowStatusModal(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                      >
                        Update Status
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedCampaign && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Update Campaign Status</h2>
              <button onClick={() => setShowStatusModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="font-medium mb-2">Campaign: {selectedCampaign.title}</p>
              <p className="mb-2">
                <span className="font-medium">Current Status: </span>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(selectedCampaign.status)}`}>
                  {selectedCampaign.status}
                </span>
              </p>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                >
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={() => handleStatusUpdate(selectedCampaign._id, newStatus)}
                disabled={processingId === selectedCampaign._id}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none disabled:bg-blue-300"
              >
                {processingId === selectedCampaign._id ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Featured Status Modal */}
      {showFeaturedModal && selectedCampaign && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Update Featured Status</h2>
              <button onClick={() => setShowFeaturedModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="font-medium mb-2">Campaign: {selectedCampaign.title}</p>
              <p className="mb-4">
                <span className="font-medium">Current Status: </span>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedCampaign.featured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {selectedCampaign.featured ? 'Featured on Homepage' : 'Not Featured'}
                </span>
              </p>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      {selectedCampaign.featured 
                        ? 'This campaign is currently featured on the homepage slider. Do you want to remove it?' 
                        : 'Do you want to feature this campaign on the homepage slider?'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowFeaturedModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={() => handleToggleFeatured(selectedCampaign._id, !selectedCampaign.featured)}
                disabled={processingId === selectedCampaign._id}
                className={`px-4 py-2 text-white rounded-md focus:outline-none disabled:opacity-50 ${selectedCampaign.featured ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {processingId === selectedCampaign._id ? 'Updating...' : selectedCampaign.featured ? 'Remove from Homepage' : 'Add to Homepage'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
